import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { Box, CircularProgress, Popover } from '@material-ui/core'
import PropTypes from 'prop-types'
import idx from 'idx'
import { tugsSelector } from 'src/store/tugs/selectors'
import {
  pilotageTugsAddRequest,
  pilotageTugsRemoveRequest,
  pilotageTugsRequest,
} from 'src/store/pilotageTugs/actions'
import {
  TugsPopover,
  TugsPopoverItem,
  TugsPopoverNoTugsAvailable,
} from 'src/components/molecules/TugsPopover'
import { pilotageTugsSelector } from 'src/store/pilotageTugs/selectors'
import { vesselSelector } from 'src/store/vessel/selectors'
import { pilotageSelector } from 'src/store/pilotage/selectors'
import { selectedPortUuidSelector } from 'src/store/ports/selectors'
import { updateVesselRequest } from 'src/store/vessel/actions'
import BollardSwlInput from './BollardSwlInput'
import TugsAndBollards from './TugsAndBollards'
import positions, { getBollardKey } from './positions'
import Ruler from 'src/components/atoms/Ruler'
import useOnlineStatus from 'src/hooks/useOnlineStatus';

const bollardSwlReleaseDate =
  process.env.REACT_APP_BOLLARD_SWL_RELEASE &&
  new Date(process.env.REACT_APP_BOLLARD_SWL_RELEASE)

const TugsDiagramWrapper = styled(Box).attrs({ p: 2 })`
  max-width: 1000px;
  margin: auto;

  svg {
    display: block;
  }
`

const BollardPopover = styled(Popover)`
  .MuiPaper-root {
    background: none;
    box-shadow: none;
  }
`

const TugsAndBollardsContainer = styled.div`
  position: relative;
  min-width: 400px;
  max-width: 1000px;
  margin: 0px auto;
`

const LegacySwlLabel = styled.div`
  width: 50px;
  height: 30px;
  position: absolute;
  top: 180px;
  left: 375px;
  text-align: center;
  font-weight: bold;
  .legacy-label {
    font-size: 10px;
    line-height: 12px;
    whitespace: nowrap;
  }
  .legacy-value {
    font-size: 14px;
    color: #fff;
  }
`

const TugLinePositions = ({ readOnly, stageUuid, pilotageTugs: pilotageTugsProp }) => {
  // If parent provides tugState/onTugStateChange, use local state for tugs and bollards, and call onTugStateChange on change
  const dispatch = useDispatch()
  const selectedPortUuid = useSelector(selectedPortUuidSelector)
  const pilotage = useSelector(pilotageSelector)
  const pilotageUuid = pilotage && pilotage.data.uuid
  const { data: vesselData } = useSelector(vesselSelector)
  const { data: tugs } = useSelector(tugsSelector)
  const pilotageTugsState = useSelector(pilotageTugsSelector)
  const pilotageTugs = pilotageTugsState && pilotageTugsState.data
  const isLoading = Boolean(pilotageTugsState && pilotageTugsState.isLoading)
  const [isSubmittingSWL, setIsSubmittingSWL] = useState(false)
  const [lastEditTime, setLastEditTime] = useState(null) // Track when edits occur
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const [editingTugPos, setEditingTugPos] = useState(null)
  const [editingBollardPos, setEditingBollardPos] = useState(null)
  const isOnline = useOnlineStatus();

  const [width, setWidth] = useState(800)

  const hasCachedTugData = Boolean(
    pilotageTugsState &&
      pilotageTugsState.pilotageUuid === pilotageUuid &&
      pilotageTugsState.data !== null
  )
  const hasExternalTugData = typeof pilotageTugsProp !== 'undefined'

  // old pilotages have a single vessel-level safeWorkLoad
  const pilotageCreated = pilotage && new Date(pilotage.data.createdAt)
  const legacySafeWorkLoad =
    bollardSwlReleaseDate &&
    pilotageCreated &&
    pilotageCreated.valueOf() < bollardSwlReleaseDate.valueOf() &&
    idx(vesselData, d => d.safeWorkLoad)

  const onTugClick = async (pos, event) => {
    if (isLoading || readOnly) {
      return;
    }
    if (activeTugs[pos]) {
      // remove
      // Find the correct pilotageTug by both stageUuid and lineNumber
      let pilotageTugUuid;
      if (stageUuid && pilotageTugsProp) {
        const pt = pilotageTugsProp.find(pt => pt.lineNumber === positions[pos].lineNumber);
        pilotageTugUuid = pt && pt.uuid;
      } else if (pilotageTugs && stageUuid) {
        const pt = pilotageTugs[stageUuid] && pilotageTugs[stageUuid][positions[pos].lineNumber];
        pilotageTugUuid = pt && pt.uuid;
      } else if (pilotageTugs) {
        // fallback for legacy/flat structure
        const pt = pilotageTugs[positions[pos].lineNumber];
        pilotageTugUuid = pt && pt.uuid;
      }
      if (!pilotageTugUuid) return;
      const success = await dispatch(
        pilotageTugsRemoveRequest(
          pilotageTugUuid,
          stageUuid,
          positions[pos].lineNumber
        )
      );
      if (success) {
        setLastEditTime(Date.now()); // Track successful edit
      }
    } else {
      setPopoverAnchor(event.target);
      setEditingTugPos(pos);
    }
  };

  const onBollardClick = async (pos, event) => {
    if (isLoading || readOnly) {
      return;
    }
    setPopoverAnchor(event.currentTarget);
    setEditingBollardPos(pos);
  };

  const onBollardSwlChange = async (pos, value) => {
    try {
      setIsSubmittingSWL(true);
      const key = getBollardKey(pos);
      await dispatch(
        updateVesselRequest(vesselData.IMO, {
          metadata: {
            ...vesselData.metadata,
            [key]: value,
          },
          port: {
            uuid: selectedPortUuid,
          },
        })
      );
    } finally {
      setIsSubmittingSWL(false);
    }
  };

  const { availableTugs, activeTugs, activeBollards, warnColors } = useMemo(() => {
    const pilotageTugsList = pilotageTugsProp || (pilotageTugs ? Object.values(pilotageTugs) : []);
    const usedTugUuids = pilotageTugsList.map(pt => pt.tug.uuid);
    const availableTugs = tugs
      ? tugs.filter(tug => usedTugUuids.indexOf(tug.uuid) === -1)
      : [];
    const activeTugs = {};
    const activeBollards = {};
    const warnColors = {};

    for (const pos in positions) {
      const { lineNumber } = positions[pos];
      // Find the pilotageTug for this lineNumber in the provided list
      const pt = pilotageTugsList.find(pt => pt.lineNumber === lineNumber);
      activeTugs[pos] = tugs && pt && tugs.find(
        tug => tug.uuid === pt.tug.uuid
      );
      activeBollards[pos] = vesselData && vesselData.metadata && vesselData.metadata[getBollardKey(pos)];
      let warnColor = false;
      if (activeTugs[pos] && activeBollards[pos]) {
        warnColor = parseFloat(activeTugs[pos].tonnage) > parseFloat(activeBollards[pos]);
      }
      warnColors[pos] = warnColor;
    }

    return {
      pilotageTugsList,
      usedTugUuids,
      availableTugs,
      activeTugs,
      activeBollards,
      warnColors
    };
  }, [pilotageTugsProp, pilotageTugs, tugs, vesselData, lastEditTime]);

  const onSelectTug = async (tugUuid, pos) => {
    setEditingTugPos(null);
    const success = await dispatch(
      pilotageTugsAddRequest(
        pilotageUuid,
        tugUuid,
        positions[pos].lineNumber,
        stageUuid
      )
    );
    if (success) {
      setLastEditTime(Date.now());
    }
  };

  useEffect(() => {
    if (!pilotageUuid) {
      return;
    }

    // When offline we rely on locally cached data and skip forcing a network refetch.
    if (lastEditTime && !isOnline) {
      return;
    }

    const shouldForceRefresh = Boolean(lastEditTime && isOnline);
    const shouldFetch =
      shouldForceRefresh ||
      !hasCachedTugData ||
      (hasExternalTugData && Boolean(lastEditTime));
    if (!shouldFetch) {
      return;
    }

    const options = shouldForceRefresh ? { forceRefresh: true } : undefined;

    const fetchData = async () => {
      try {
        await dispatch(pilotageTugsRequest(pilotageUuid, options));
      } catch (error) {
        console.error('Error fetching pilotage tugs:', error);
      }
    };

    fetchData();
  }, [dispatch, lastEditTime, pilotageUuid, isOnline, hasExternalTugData, hasCachedTugData]);

  const isDoingAction = isLoading || isSubmittingSWL
  return (
    <div style={{ margin: '-50px -30px -50px -50px', position: 'relative' }}>
      {isDoingAction && (
        <div style={{ "position": 'absolute', zIndex: 1000, height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </div>
      )}

      <TugsDiagramWrapper>
        <TugsAndBollardsContainer>

          <Ruler onChange={setWidth} />

          <div
            style={{
              width: 800,
              height: 400,
              transform: `scale(${width / 800}, ${(width / 2) / 400})`,
              transformOrigin: 'top left',
              top: 0,
              left: 0,
              position: 'absolute',
              zIndex: -1
            }}>
            {
              // show legacy if present, and disabled swl controls
              legacySafeWorkLoad &&
              <LegacySwlLabel>
                <div className="legacy-label">Max SWL</div>
                <div className="legacy-value">{legacySafeWorkLoad}t</div>
              </LegacySwlLabel>
            }
          </div>

          <TugsAndBollards
            readOnly={readOnly}
            activeTugs={activeTugs}
            warnColors={warnColors}
            onTugClick={onTugClick}
            isLoading={isDoingAction}
            activeBollards={
              // bollard swl controls disabled for legacy
              legacySafeWorkLoad ? activeTugs : activeBollards
            }
            onBollardClick={
              legacySafeWorkLoad ? (() => null) : onBollardClick
            }
          />

        </TugsAndBollardsContainer>
        <Box position="relative">
          {editingTugPos && (
            <TugsPopover
              open
              anchorEl={popoverAnchor}
              onBackdropClick={() => setEditingTugPos(null)}
            >
              {availableTugs.length > 0 ? (
                availableTugs.map(tug => {
                  const { uuid, name, tonnage } = tug
                  return (
                    <TugsPopoverItem
                      key={uuid}
                      name={name}
                      tonnage={tonnage}
                      onClick={() => onSelectTug(uuid, editingTugPos)}
                    />
                  )
                })
              ) : (
                <TugsPopoverNoTugsAvailable />
              )}
            </TugsPopover>
          )}
          {
            editingBollardPos &&
            <BollardPopover
              open
              anchorEl={popoverAnchor}
              anchorOrigin={positions[editingBollardPos].bollardEditAnchorOrigin}
              transformOrigin={positions[editingBollardPos].bollardEditTransformOrigin}
              onBackdropClick={() => setEditingBollardPos(null)}
            >
              <BollardSwlInput
                value={activeBollards[editingBollardPos] || ''}
                readOnly={readOnly}
                onChange={(value) => {
                  onBollardSwlChange(editingBollardPos, value)
                  setEditingBollardPos(null)
                }}
              />
            </BollardPopover>
          }
        </Box>

      </TugsDiagramWrapper>
    </div>
  )
}

TugLinePositions.propTypes = {
  readOnly: PropTypes.bool,
  stageUuid: PropTypes.string,
  pilotageTugs: PropTypes.array,
}

export default TugLinePositions
